import { DT } from "../../constants.ts";
import { add, clamp, norm, scale } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, MoveKind, hv, setHv } from "../../sim/components.ts";
import { aim, applyDamage, applyStatus, explode, isEnemy, moveProjectile, playersInRadius, spawnProjectile } from "../../sim/combat.ts";
import { damageBarrier } from "../../sim/combat.ts";
import { damageConstruct, spawnConstruct } from "../../sim/constructs.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { ck, fxk, pk, type HeroModule } from "../module.ts";
import { action, commonInput, consume, ownerAlive, ready, secs, spreadDir, startMove, useUlt, wishDir } from "../kitlib.ts";

export const V = { draw: 0, focus: 1, leapt: 2, ultPulse: 3 } as const;
export const PK = { Piercing: pk(HeroId.Squawkeye, 0), Blast: pk(HeroId.Squawkeye, 1), Hyper: pk(HeroId.Squawkeye, 2) } as const;
export const CK = { Afterimage: ck(HeroId.Squawkeye, 0) } as const;
export const FX = { Arrow: fxk(HeroId.Squawkeye, 0), Blast: fxk(HeroId.Squawkeye, 1), Hyper: fxk(HeroId.Squawkeye, 2), Slash: fxk(HeroId.Squawkeye, 3) } as const;

const def: HeroDef = {
  id: HeroId.Squawkeye,
  slug: "squawkeye",
  name: "Squawkeye",
  role: Role.Damage,
  color: "#8e44ad",
  blurb: "Bow duelist who rewards held draw, focused aim, trick arrows and afterimage picks.",
  health: 250,
  armor: 0,
  shields: 0,
  radius: 0.4,
  height: 1.85,
  headRadius: 0.21,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 0,
  reloadTime: 0,
  ultCost: 3100,
  abilities: {
    [Slot.Primary]: { name: "Piercing Squawk", key: "LMB (hold)", description: "Draw and release a charged arcing arrow.", cooldown: 0 },
    [Slot.Secondary]: { name: "Nest Burst", key: "RMB", description: "Fire three explosive arrows with three stored charges.", cooldown: 0.5, charges: 3 },
    [Slot.Ability1]: { name: "Crescent Caw", key: "Shift", description: "Slash forward with a blade and launch enemies.", cooldown: 12 },
    [Slot.Ability2]: { name: "Hypersonic Hoot", key: "E", description: "Fire a fast line arrow that hits twice and slows.", cooldown: 12 },
    [Slot.Ability3]: { name: "Skyward Scramble", key: "F/Space", description: "Double-jump in your movement direction.", cooldown: 6 },
    [Slot.Ultimate]: { name: "Hunter's Hindsight", key: "Q", description: "Mark afterimages; damage to marked foes echoes.", cooldown: 0 },
  },
  passive: { name: "Archer's Squint", description: "Aiming near enemies builds focus, adding damage to charged primary arrows." },
};

function fireArrow(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, kind: number, speed: number, damage: number, input: Parameters<HeroModule["kit"]["tick"]>[2], radius = 0.08, gravity = 7) {
  const a = aim(sim, eid);
  const p = spawnProjectile(sim, eid, { kind, origin: add(a.origin, scale(a.dir, 0.5)), vel: scale(a.dir, speed), gravity, radius, seconds: 2, seq: input.seq });
  if (p !== undefined) sim.c.Projectile.target[p] = Math.round(clamp(damage, 0, 255) * 10);
}

function spawnAfterimages(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number) {
  const a = aim(sim, eid, 40);
  for (const { eid: t } of playersInRadius(sim, add(a.origin, scale(a.dir, 20)), 22, (e) => isEnemy(sim, eid, e), true)) {
    const old = sim.rewindTick;
    for (const dt of [12, 28, 44]) {
      sim.rewindTick = Math.max(0, sim.tick - dt);
      const hist = sim.targetPos(t);
      if (!hist.alive) continue;
      const img = spawnConstruct(sim, t, CK.Afterimage, hist.pos, sim.c.Look.yaw[t]!, { seconds: 3, hp: 80, v0: sim.c.Net.id[t]!, ownerDeath: "persist" });
      if (img !== undefined) sim.c.Construct.v0[img] = sim.c.Net.id[t]!;
    }
    sim.rewindTick = old;
  }
}

const module: HeroModule = {
  def,
  kit: {
    init() {},
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      commonInput(sim, eid, input, pressed);
      if (hv(c, eid, V.leapt) > 0 && c.Player.grounded[eid]) setHv(c, eid, V.leapt, 0);
      if (!canAct) return;

      const drawing = !!(input.buttons & Btn.Primary);
      const draw = hv(c, eid, V.draw);
      if (drawing) {
        setHv(c, eid, V.draw, Math.min(0.9, draw + DT * (c.Ult.active[eid]! > 0 ? 1.5 : 1)));
        setHv(c, eid, V.focus, Math.min(0.9, hv(c, eid, V.focus) + DT));
        action(sim, eid, ActionKind.Fire, 2);
      } else if (draw > 0) {
        const k = clamp(draw / 0.9, 0, 1);
        const focus = clamp(hv(c, eid, V.focus) / 0.9, 0, 1);
        fireArrow(sim, eid, PK.Piercing, 130 + 65 * k, 28 + 42 * k + 90 * focus, input, 0.09, 6);
        c.Weapon.fireCd[eid] = secs(0.18);
        action(sim, eid, ActionKind.Fire, 8);
        setHv(c, eid, V.draw, 0);
        setHv(c, eid, V.focus, Math.max(0, hv(c, eid, V.focus) - 0.35));
      } else setHv(c, eid, V.focus, Math.max(0, hv(c, eid, V.focus) - DT * 0.32));

      if (useUlt(sim, eid, input, pressed)) {
        c.Ult.active[eid] = secs(10);
        spawnAfterimages(sim, eid);
      }
      if (c.Ult.active[eid]! > 0 && sim.tick % 30 === 0) spawnAfterimages(sim, eid);

      if (pressed & Btn.Secondary && ready(sim, eid, Slot.Secondary)) {
        consume(sim, eid, Slot.Secondary);
        action(sim, eid, ActionKind.AltFire, 12);
        const a = aim(sim, eid);
        for (let i = 0; i < 3; i++) {
          const dir = spreadDir(a.dir, 0.199, c.Weapon.shots[eid]! * 31 + i * 19 + 7);
          const p = spawnProjectile(sim, eid, { kind: PK.Blast, origin: add(a.origin, scale(dir, 0.4)), vel: scale(dir, 60), gravity: 9, radius: 0.1, seconds: 1.6, seq: input.seq + i });
          if (p !== undefined) c.Projectile.bounces[p] = 16;
        }
      }
      if (pressed & Btn.Ability1 && ready(sim, eid, Slot.Ability1)) {
        consume(sim, eid, Slot.Ability1);
        action(sim, eid, ActionKind.Melee, 12);
        const d = wishDir(input);
        startMove(sim, eid, MoveKind.Dash, 12, 18, { x: d.x * 18, y: 0, z: d.z * 18 });
        const center = add(sim.posOf(eid), { x: 0, y: 1, z: 0 });
        sim.emit({ type: "explosion", pos: center, radius: 8, kind: FX.Slash });
        for (const { eid: t } of playersInRadius(sim, center, 8, (e) => isEnemy(sim, eid, e))) {
          applyDamage(sim, t, 40, eid, { pos: center });
          c.Vel.y[t] = Math.max(c.Vel.y[t]!, 6);
          c.Player.grounded[t] = 0;
        }
      }
      if (pressed & Btn.Ability2 && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        action(sim, eid, ActionKind.Cast, 10);
        fireArrow(sim, eid, PK.Hyper, 150, 55, input, 0.18, 0);
      }
      if ((pressed & (Btn.Ability3 | Btn.Jump)) && ready(sim, eid, Slot.Ability3) && !c.Player.grounded[eid] && hv(c, eid, V.leapt) <= 0) {
        consume(sim, eid, Slot.Ability3);
        action(sim, eid, ActionKind.Cast, 8);
        const d = wishDir(input);
        c.Vel.x[eid] = d.x * 9;
        c.Vel.z[eid] = d.z * 9;
        c.Vel.y[eid] = 8;
        setHv(c, eid, V.leapt, 1);
      }
    },
    movement(sim, eid, input, mods) {
      if (input.buttons & Btn.Primary) mods.speedMul *= 0.8;
      void sim;
      void eid;
    },
  },
  constructs: {
    [CK.Afterimage]: {
      maxHp: 80,
      seconds: 3,
      shape: { kind: "cyl", r: 0.38, h: 1.8 },
      solid: false,
      blocksShots: false,
      damageable: true,
      persistOnOwnerDeath: true,
      visual: { color: "#d7a7ff", shape: "cyl" },
      onDamage(sim, c, amount, attacker) {
        const target = sim.eidOf(sim.c.Construct.v0[c]!);
        if (target !== undefined && sim.c.Player.alive[target]) applyDamage(sim, target, amount, attacker, { quiet: true, pos: add(sim.posOf(target), { x: 0, y: 1, z: 0 }), info: { kind: "other", redirected: true, sourceKind: CK.Afterimage } });
      },
    },
  },
  projectiles: {
    [PK.Piercing]: {
      pierce: 1,
      visual: { color: "#d7a7ff", size: 0.11, trail: true },
      tick(sim, p) {
        const owner = sim.c.Projectile.owner[p]!;
        const hit = moveProjectile(sim, p);
        if (!hit) {
          if (sim.c.Projectile.ticks[p]! <= 0) sim.destroyEntity(p);
          return;
        }
        if (ownerAlive(sim, owner)) {
          const dmg = (sim.c.Projectile.target[p]! || 700) / 10;
          if (hit.target !== undefined) applyDamage(sim, hit.target, dmg, owner, { crit: hit.head, pos: hit.pos, info: { kind: "projectile", sourceKind: PK.Piercing } });
          else if (hit.construct !== undefined) damageConstruct(sim, hit.construct, dmg, owner);
          else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, dmg, owner);
          sim.emit({ type: "impact", pos: hit.pos, kind: FX.Arrow });
        }
        if ((hit.target !== undefined || hit.construct !== undefined) && sim.c.Projectile.bounces[p]! < 1) {
          sim.c.Projectile.bounces[p] = sim.c.Projectile.bounces[p]! + 1;
          const d = norm({ x: sim.c.Vel.x[p]!, y: sim.c.Vel.y[p]!, z: sim.c.Vel.z[p]! });
          const next = add(hit.pos, scale(d, 2.2));
          sim.c.Pos.x[p] = next.x;
          sim.c.Pos.y[p] = next.y;
          sim.c.Pos.z[p] = next.z;
          return;
        }
        sim.destroyEntity(p);
      },
    },
    [PK.Blast]: {
      visual: { color: "#ffb347", size: 0.13, trail: true },
      tick(sim, p) {
        const owner = sim.c.Projectile.owner[p]!;
        const hit = moveProjectile(sim, p);
        if (!hit) {
          if (sim.c.Projectile.ticks[p]! <= 0) sim.destroyEntity(p);
          return;
        }
        if (ownerAlive(sim, owner)) {
          if (hit.target !== undefined) applyDamage(sim, hit.target, 16, owner, { pos: hit.pos, info: { kind: "projectile", sourceKind: PK.Blast } });
          explode(sim, owner, hit.pos, { radius: 3, damage: 34, minDamage: 12, fxKind: FX.Blast, skip: hit.target });
        }
        sim.destroyEntity(p);
      },
    },
    [PK.Hyper]: {
      visual: { color: "#8ee8ff", size: 0.16, trail: true },
      tick(sim, p) {
        const owner = sim.c.Projectile.owner[p]!;
        const hit = moveProjectile(sim, p);
        if (!hit) {
          if (sim.c.Projectile.ticks[p]! <= 0) sim.destroyEntity(p);
          return;
        }
        if (hit.target !== undefined && ownerAlive(sim, owner)) {
          applyDamage(sim, hit.target, 55, owner, { pos: hit.pos, info: { kind: "projectile", sourceKind: PK.Hyper } });
          applyDamage(sim, hit.target, 55, owner, { quiet: true, pos: hit.pos, info: { kind: "projectile", sourceKind: PK.Hyper } });
          applyStatus(sim, hit.target, "slow", 1, { slowPct: 0.4, source: owner });
          if (!sim.c.Player.grounded[hit.target]) applyStatus(sim, hit.target, "knockdown", 0.5, { source: owner });
        }
        sim.emit({ type: "impact", pos: hit.pos, kind: FX.Hyper });
        sim.destroyEntity(p);
      },
    },
  },
  fx: {
    [FX.Arrow]: { color: "#d7a7ff" },
    [FX.Blast]: { color: "#ffb347" },
    [FX.Hyper]: { color: "#8ee8ff" },
    [FX.Slash]: { color: "#d7a7ff" },
  },
};

export default module;
